import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Subject, of } from 'rxjs';
import { catchError, concatMap, finalize, first, map } from 'rxjs/operators';
import {
    IDataPackage,
    IDataPackageApi,
    IDataPackageConfigProps,
    IDataPackageFileDescriptor
} from '@vivli/shared/features/data-package/infrastructure/interface';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { LoggerService, uploadActivityService } from '@vivli/shared/infrastructure/service';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { downloadFile as downloadFileToBrowser, useErrorHandling } from '@vivli/shared/infrastructure/hook';
import { IFileUploader, NETWORK_ERROR_PREFIX } from '@vivli/shared/infrastructure/hook';
import moment from 'moment';
import { sanitizeUploadFileName } from './sanitize-upload-filename';
import { isFileUploadComplete, isFileUploadFailed, isFileUploadInProgress } from './data-package-file-status';
import { v4 as uuidv4 } from 'uuid';
import { IFileExtension } from '@vivli/shared/infrastructure/interface';
import {
    useActiveUserContext,
    useDataPackageApiContext,
    useDataPackageFileApiContext,
    useModalApiContext,
    useOrganizationsApiContext,
    useStudiesApiContext,
    useToastApiContext,
    useVivliConfigContext
} from '@vivli/core/infrastructure/context';
import { DataPackageValidationModeEnum } from '@vivli/features/studies/infrastructure/enum';

export interface IDataPackageInternalApi {
    onPubliclyAvailableFilesChanged: (files: IDataPackageFileDescriptor[]) => void;
}

// Tagged union of operations carried by the serialized file-operation queue.
// `update` runs addUpdateFile; `delete` runs deleteFile. Both resolve to the
// updated IDataPackage so the consumer can apply local state uniformly.
type FileQueueOp = {
    kind: 'update' | 'delete';
};

interface FileQueueItem {
    op: FileQueueOp;
    // Enqueue-time snapshot of the file. Used for identity (name) and as a fallback when the
    // file is no longer in local state. For `update` ops this is NOT the write payload — see
    // `changes` and the dequeue-time merge in the consumer below. (DEV-711)
    file: IDataPackageFileDescriptor;
    // `update` ops only: just the fields this producer intends to change.
    changes?: Partial<IDataPackageFileDescriptor>;
    errorMessage: string;
    onSuccess?: (dp: IDataPackage, file: IDataPackageFileDescriptor) => void;
    // Apply the merged payload to local state even when the server write fails. Opt-in, and
    // meaningful only for `update` ops. Needed by the upload-error write, which reports a
    // network failure over the very network that just failed — see onFileUploadError. (DEV-478)
    applyLocallyOnFailure?: boolean;
    // Skip the error modal when the write fails; the producer owns user feedback instead. (DEV-478)
    suppressErrorModal?: boolean;
}

// Optional behaviour for a queued write, kept as an options bag so _serverFileUpdate does not
// grow yet another positional argument. Derived from FileQueueItem so the two cannot drift.
type IServerFileUpdateOptions = Pick<FileQueueItem, 'applyLocallyOnFailure' | 'suppressErrorModal'>;

// Delete ops take the same options bag minus applyLocallyOnFailure, which is meaningless for a delete
// (the consumer only re-applies local state for `update`). Needed by the cancel path, where a missing
// descriptor is an expected outcome rather than something to pop a modal over. (DEV-746)
type IServerFileDeleteOptions = Pick<FileQueueItem, 'suppressErrorModal'>;

interface UseDataPackageProps extends IDataPackageConfigProps {
    dataPackageId: string;
    secondaryDataPackageId?: string;
    publiclyAvailableOrgId?: string;
    readOnly?: boolean;
    onDataPackageReady?: (dataPackage: IDataPackage) => void;
}

export interface IDataPackageHookApi {
    dataPackage: IDataPackage | undefined;
    files: IDataPackageFileDescriptor[] | undefined;
    publiclyAvailableOrg: IOrganization | undefined;
    isLoading: boolean;
    fileLimitReached: boolean;
    hasRequiredTypes: boolean;
    // Any file failed or still uploading, of any type. Gates Submit alongside hasRequiredTypes. (DEV-746)
    hasIncompleteUploads: boolean;
    isFilePending: (name: string) => boolean;
    getDataPackage: () => void;
    setDataPackage: (dataPackage: IDataPackage) => void;
    setAdditionalFileProps: (additionalFileProps: any) => void;

    onPreExistingFile?: (name: string) => Promise<boolean>;
    onFileDeleted: (file: IDataPackageFileDescriptor) => Promise<void>;
    onFileDownload?: (file: IDataPackageFileDescriptor) => void;
    onFileTypeChanged?: (file: IDataPackageFileDescriptor, newFileType: string) => void;
    onFileUploadCompleted?: (id: string, name: string) => void;
    onFileUploadError?: (id: string, name: string, error: string) => void;
    onFileUploadStart?: (id: string, fileName: string, fileSizeKb: number) => void;
    onFileUploadProgress?: (id: string, name: string, percentUploaded: number) => void;
    onFileUploadCancelled?: (file: IDataPackageFileDescriptor) => void;
    onFileUploadRetry?: (file: IDataPackageFileDescriptor) => void;
    subscribeFileProgress?: (id: string, cb: (pct: number) => void) => () => void;
    onUploaderInit?: (api: IFileUploader) => void;
    onAllFilesCompleted?: (succeededFileIds: string[], failedFileIds: string[]) => void;
    onFileValidation?: (name: string) => boolean | Promise<boolean>;
    onPubliclyAvailableChanged?: (file: IDataPackageFileDescriptor, isPubliclyAvailable: boolean) => void;
}

export const useDataPackage = ({
    dataPackageId,
    secondaryDataPackageId,
    onSecondaryDataPackageUpdated,
    secondaryFilesToShow,
    publiclyAvailableOrgId,
    readOnly,
    requiredTypes,
    validationMode,
    showDeleteDialog,
    onDataPackageReady,
    onValidation,
    onFileAdded,
    onFileRemoved,
    recordFileDownload,
    datasetMetaDataDoi,
    studyOrgId,
    showPubliclyAvailable,
    publicAccessByDefaultFileTypes,
    sortFilesByDate,
    fileLimit,
    fileExtensions,
    onFileExtensionWarning,
    showFileWarning,
    onPackageUpdated,
    overrideDataPackage,
    useSecureStorage = true
}: UseDataPackageProps): IDataPackageHookApi => {
    const initializedRef = useRef(false);

    const _dataPackageService = useDataPackageApiContext();
    const _dataPackageFileService = useDataPackageFileApiContext();
    const _modalService = useModalApiContext();
    const _toastService = useToastApiContext();
    const { handleError } = useErrorHandling();
    const _orgsService = useOrganizationsApiContext();
    const _studiesService = useStudiesApiContext();
    const { user, orgHasPermission } = useActiveUserContext();
    const config = useVivliConfigContext();

    const dataPackageRef = useRef<IDataPackage>(null);
    const fileExtensionsRef = useRef<IFileExtension[]>([]);
    const additionalFilePropsRef = useRef<any>({});
    // Live uploader API captured from FileUploaderView via onUploaderInit. Used to
    // abort/dequeue an in-progress (or queued) upload when the user cancels.
    const uploaderApiRef = useRef<IFileUploader>(null);

    // ──────────────────── Upload-progress pub/sub ────────────────────
    // Per-tick upload progress is published through this ref-based channel rather than
    // through the `files` state. XHR progress events fire many times per second; if each
    // one updated `files` it would create a new array reference, refresh the grid row, and
    // tear down/recreate the controls cell — which makes the Cancel (X) button drop single
    // clicks in Firefox (mousedown/mouseup land on different DOM nodes). Routing progress
    // here keeps the row data (and therefore the Cancel button's DOM) stable; only the small
    // UploadProgressBar component subscribed to a given uploaderId re-renders. (DEV-479)
    const progressRef = useRef<Record<string, number>>({});
    const progressSubscribersRef = useRef<Record<string, Set<(pct: number) => void>>>({});

    const emitFileProgress = (id: string, pct: number) => {
        progressRef.current[id] = pct;
        progressSubscribersRef.current[id]?.forEach((cb) => cb(pct));
    };

    // Subscribe a callback to progress updates for a single uploaderId. Immediately invokes
    // the callback with the latest known value so a bar that mounts after the first tick is
    // in sync, and returns an unsubscribe function for useEffect cleanup.
    // Identity is kept stable (closes over refs only) so the subscribing bar's layout effect does
    // not tear down and re-subscribe on every render of this hook's consumer. (DEV-711)
    const subscribeFileProgress = useCallback((id: string, cb: (pct: number) => void) => {
        const subscribers = progressSubscribersRef.current[id] ?? new Set();
        subscribers.add(cb);
        progressSubscribersRef.current[id] = subscribers;
        cb(progressRef.current[id] ?? 0);

        return () => {
            const set = progressSubscribersRef.current[id];
            if (!set) {
                return;
            }
            set.delete(cb);
            if (set.size === 0) {
                delete progressSubscribersRef.current[id];
            }
        };
    }, []);

    // Drops the stored progress value for a finished/cancelled upload so the map does not
    // grow unbounded.
    // Deliberately does NOT touch progressSubscribersRef: subscribers own their own lifecycle via the
    // unsubscribe returned from subscribeFileProgress. Deleting the Set here orphaned the still-mounted
    // bar's setter (its cleanup then found no Set and no-op'd), so every later re-subscribe resolved to
    // `?? 0` and the bar re-animated from zero. (DEV-711)
    const clearFileProgress = (id: string) => {
        delete progressRef.current[id];
    };

    // ──────────────────── Serialized file-operation queue ────────────────────
    // addUpdateFile and deleteFile are both optimistic-concurrency (etag) writes
    // against the data package. If two of them fire concurrently (e.g. a type
    // change overlaps an upload-complete, or a delete overlaps a pending update)
    // they race on the same etag and one clobbers the other. To prevent that we
    // funnel EVERY such write through this RxJS Subject and process emissions with
    // `concatMap`, which guarantees each op fully completes before the next one
    // starts — turning concurrent writes into a strict serial pipeline.
    //
    // The queue carries a tagged union of operations: an `update` (addUpdateFile)
    // or a `delete` (deleteFile). Producers call `_serverFileUpdate(...)` or
    // `_serverFileDelete(...)`. The single subscriber set up in the useEffect below
    // is the consumer and branches on `op.kind`.
    const fileUpdateQueueRef = useRef<Subject<FileQueueItem>>(new Subject());

    const [dataPackage, setDataPackageState] = useState<IDataPackage>();
    const [publiclyAvailableOrg, setPubliclyAvailableOrg] = useState<IOrganization>();
    const [isLoading, setIsLoadingState] = useState(false);
    const setIsLoading = (loading: boolean) => {
        setIsLoadingState(loading);
    };

    // Per-row in-flight tracking. Holds the file names that currently have a queued
    // server operation (update or delete) in flight, so the grid can disable just
    // those rows instead of showing a full-grid loading overlay. Uploads are NOT
    // tracked here — an in-progress upload row stays interactive so its Cancel
    // button keeps working.
    const [pendingFileNames, setPendingFileNames] = useState<string[]>([]);
    const _addPendingFile = (name: string) => setPendingFileNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
    const _removePendingFile = (name: string) => setPendingFileNames((prev) => prev.filter((n) => n !== name));
    const isFilePending = (name: string) => pendingFileNames.includes(name);

    useEffect(() => {
        // Consumer side of the serialized file-update queue. A single subscription
        // drains fileUpdateQueueRef. `concatMap` is the key operator: it waits for
        // the inner addUpdateFile observable to complete before it pulls the next
        // queued item, so updates are applied one-at-a-time in arrival order and
        // never overlap. Errors are caught inside the inner pipe (mapped to a null
        // dp) so one failed update doesn't tear down the subscription and stall the
        // rest of the queue. The subscription is torn down on unmount / dependency
        // change to avoid leaks and stale closures over dataPackageId.
        const subscription = fileUpdateQueueRef.current
            .pipe(
                concatMap(({ op, file, changes, errorMessage, onSuccess, applyLocallyOnFailure, suppressErrorModal }) => {
                    // Resolve the write payload against live state at DEQUEUE time, not enqueue time.
                    // Producers pass only the fields they mean to change; everything else comes from the
                    // freshest descriptor we hold. PutFileDescriptor replaces the descriptor wholesale
                    // server-side, so a payload built at enqueue time could re-persist values that a
                    // write ahead of it in this queue had already superseded — e.g. a file-type change
                    // chosen while that file's upload-completion write was still queued landed last and
                    // rewrote isComplete:false, leaving the row stuck on "Uploading" and showing "Failed"
                    // after a reload. Merging here also stops the symmetric loss (a completion write
                    // reverting a pending type change back to Unknown). (DEV-711)
                    const latest = filesRef.current?.find((f) => f.name === file.name);
                    const updatedFile = op.kind === 'update' ? { ...(latest ?? file), ...changes } : file;

                    // The resolved payload is `{ ...latest, ...changes }`, so it is content-identical to
                    // what local state already holds whenever every changed key already matches — which is
                    // the normal case for the upload-start write, since local state was updated before it
                    // was even enqueued. Re-applying it would hand the grid a new row object for no reason,
                    // and ag-grid would destroy and recreate the cell, remounting the progress bar
                    // mid-upload and restarting its 0→100 animation. (DEV-711)
                    const changesAlreadyApplied =
                        !!latest && !!changes && Object.keys(changes).every((key) => latest[key] === changes[key]);

                    // Per-row disabling: mark this file in-flight (not the whole grid).
                    _addPendingFile(updatedFile.name);
                    const request =
                        op.kind === 'update'
                            ? _dataPackageService.addUpdateFile(dataPackageId, updatedFile)
                            : _dataPackageFileService.deleteFile(dataPackageId, updatedFile.name, true);
                    return request.pipe(
                        first(),
                        map((dp) => ({
                            dp,
                            op,
                            updatedFile,
                            changesAlreadyApplied,
                            onSuccess,
                            applyLocallyOnFailure,
                            latestDate: latest?.updatedDateTime
                        })),
                        catchError((err) => {
                            // Producers that own their own user feedback opt out of the modal — a failed
                            // upload already renders as Failed + Retry on the row, so a blocking popup
                            // just stands between the user and the button. (DEV-478)
                            if (suppressErrorModal) {
                                LoggerService.log(`${errorMessage} ${err}`);
                            } else {
                                _handleError(errorMessage, err);
                            }
                            // emit a null dp so the stream continues with the next queued op
                            return of({
                                dp: null,
                                op,
                                updatedFile,
                                changesAlreadyApplied,
                                onSuccess,
                                applyLocallyOnFailure,
                                latestDate: latest?.updatedDateTime
                            });
                        }),
                        finalize(() => _removePendingFile(updatedFile.name))
                    );
                })
            )
            .subscribe(({ dp, op, updatedFile, changesAlreadyApplied, onSuccess, applyLocallyOnFailure, latestDate }: any) => {
                // only apply local state when the server write succeeded (dp != null)
                if (!dp) {
                    // ...unless the producer explicitly asked for the local change to stick anyway.
                    // concatMap still guarantees ordering, so an opted-in write applied here cannot be
                    // reverted by a write that was queued before it. (DEV-478)
                    if (applyLocallyOnFailure && op.kind === 'update') {
                        _updateFile(updatedFile);
                    }
                    return;
                }

                setDataPackage(dp);

                // updatedDateTime is stamped server-side by PutFileDescriptor on every write and comes
                // back on the returned package, but the payload we built locally has never carried it —
                // so the Uploaded Date cell stayed blank until a full getDataPackage refetch, i.e. until
                // the user exited and reopened the package. Read the server's value back here. (DEV-493)
                //
                // Gated on the upload being finished. The server re-stamps the date on the upload-START
                // write too, and applying that would hand ag-grid a new row object mid-upload, destroying
                // and recreating the cell and remounting the progress bar — exactly what the
                // `changesAlreadyApplied` short-circuit above exists to prevent. Completion and every
                // post-upload write (type change, publicly-available toggle) still pick the date up.
                const serverDate =
                    op.kind === 'update'
                        ? dp.dataPackageFileDescriptors?.find((f) => f.name === updatedFile.name)?.updatedDateTime
                        : undefined;
                const applied =
                    serverDate && isFileUploadComplete(updatedFile) ? { ...updatedFile, updatedDateTime: serverDate } : updatedFile;

                if (op.kind === 'delete') {
                    // remove the row + keep the file count accurate.
                    // Decrement only when the row is still present: the cancel path removes the row and
                    // decrements up front so the row disappears without waiting on the server, and this
                    // branch must not decrement a second time for the same file. (DEV-746)
                    const stillPresent = (filesRef.current || []).some((f) => f.name === updatedFile.name);
                    if (stillPresent) {
                        filesRefCount.current = Math.max(0, filesRefCount.current - 1);
                        _setFiles((filesRef.current || []).filter((f) => f.name !== updatedFile.name));
                    }
                    onFileRemoved && onFileRemoved(updatedFile);
                } else if (!changesAlreadyApplied || applied.updatedDateTime !== latestDate) {
                    // A newly-arrived server date has to get through even when every `changes` key already
                    // matched local state, or the completion write — whose changes are typically already
                    // applied — would drop the date it just fetched.
                    _updateFile(applied);
                }

                onSuccess && onSuccess(dp, applied);
            });

        return () => subscription.unsubscribe();
    }, [dataPackageId, _dataPackageService]);
    const [hasRequiredTypes, setHasRequiredTypes] = useState<boolean>(false);

    const filesRef = useRef<IDataPackageFileDescriptor[]>();
    const filesRefCount = useRef(0);
    const [files, setFiles] = useState<IDataPackageFileDescriptor[]>();
    const [fileLimitReached, setFileLimitReached] = useState<boolean>(false);

    const _setFiles = (updatedFiles: IDataPackageFileDescriptor[]) => {
        // update the files ref state (needed for quick async uploads of multiple files
        filesRef.current = updatedFiles;

        // update the file limit if we have reached it
        _updateFileLimitReached();

        // set the files state to update screens
        setFiles(updatedFiles);
    };

    const _updateFile = (file: IDataPackageFileDescriptor) => {
        const updatedFiles = filesRef.current?.map((f) => (f.name === file.name ? file : f));
        _setFiles(updatedFiles);
    };

    const setDataPackage = (dataPackage: IDataPackage): void => {
        dataPackageRef.current = dataPackage;
        setDataPackageState(dataPackage);
        onPackageUpdated && onPackageUpdated(dataPackage);
    };

    const _handleError = (customMessage: string, error) => {
        handleError(customMessage, error);
        setIsLoading(false);
    };

    const _getPubliclyAvailableOrg = () => {
        if (publiclyAvailableOrgId !== 'FakeOrgId') {
            _orgsService
                .getOrganization(publiclyAvailableOrgId)
                .pipe(first())
                .subscribe({
                    next: setPubliclyAvailableOrg,
                    error: (error) => _handleError('Error getting organization.', error)
                });
        }
    };

    // A required type is only satisfied by a file whose upload actually finished. Matching on
    // `type` alone let the Submit button enable while uploads were still in flight — the descriptor
    // is persisted with `isComplete: false` as soon as the upload starts, so assigning it a type was
    // enough to look satisfied — and it stayed enabled once those uploads failed (DEV-699).
    // Read the server-state descriptors rather than the local `files` array: the server copy still
    // carries `isComplete: false` for an upload that never finished, which covers the after-reload
    // case without depending on the client-side reconciliation in getDataPackage.
    const _hasCompletedFileOfType = (type: string) =>
        dataPackage?.dataPackageFileDescriptors?.some((file) => file.type === type && isFileUploadComplete(file));

    const _validateRequiredTypes = () => {
        if (!requiredTypes) {
            setHasRequiredTypes(true);
            return;
        }

        let _hasRequiredTypes = false;
        switch (validationMode) {
            case DataPackageValidationModeEnum.All:
                _hasRequiredTypes = requiredTypes.every(_hasCompletedFileOfType);
                break;
            case DataPackageValidationModeEnum.Any:
                _hasRequiredTypes = requiredTypes.some(_hasCompletedFileOfType);
                break;
        }

        setHasRequiredTypes(_hasRequiredTypes);
        onValidation && onValidation(_hasRequiredTypes);
    };

    const _handleFileDeleteWarning = (file: IDataPackageFileDescriptor) => {
        const isUploadedByCurrentUser = file.uploadedByUserId === user.userId;
        const modalMessage = isUploadedByCurrentUser
            ? `Are you sure you want to delete this file:  ${file.name}`
            : `This file was added by another user - are you sure you want to delete this file: ${file.name}`;

        return new Promise<void>((resolve, reject) => {
            _modalService.confirm(modalMessage, {
                confirmText: 'Ok',
                cancelText: 'Cancel',
                onConfirm: () => resolve(),
                onCancel: () => setIsLoading(false)
            });
        });
    };

    const _canUserSendDataCount = (studyOrgId: string) => {
        // when data contributor downloads its own data , it doesn't count
        //TODO PERMFE - redo with permissions+
        if (studyOrgId) {
            const orgs = user.orgMemberships?.filter((org) => {
                orgHasPermission(studyOrgId, PermissionsEnum.DataContrib);
            });
            return !(orgs && orgs.length > 0);
        }
        return false;
    };

    // Derives the public-access flags implied by a newly chosen file type. Results are written into
    // `changes` rather than onto `file`: callers now hand in the descriptor the UI is holding (e.g. the
    // grid row's data), and mutating that in place would corrupt the row. Reads fall through to `file`
    // until this function has written the field, preserving the previous read-your-own-writes behaviour
    // when it mutated a throwaway spread. (DEV-711)
    const _handleFileTypeChangeForPubliclyAvailable = (
        type: string,
        file: IDataPackageFileDescriptor,
        changes: Partial<IDataPackageFileDescriptor>,
        fromUser?: boolean
    ) => {
        if (!showPubliclyAvailable) {
            return;
        }

        const isDisabled = () => changes.publicAccessDisabled ?? file.publicAccessDisabled;
        const isGranted = () => changes.publicAccessGranted ?? file.publicAccessGranted;

        if (type === 'Unknown') {
            changes.publicAccessDisabled = true;
            changes.publicAccessGranted = false;
        }

        if (config?.ipdFileTypes) {
            //clear and disable checkbox if not shareable
            changes.publicAccessDisabled = !config.ipdFileTypes.find((t) => t.fileType === type)?.publicAccessAllowable;
            if (changes.publicAccessDisabled) {
                changes.publicAccessGranted = false;
            }
        }

        //if file type is shared by default by organization, set it to true - only on user's action
        if (fromUser && !isDisabled() && publicAccessByDefaultFileTypes?.length > 0 && !isGranted()) {
            changes.publicAccessGranted = !!publicAccessByDefaultFileTypes.find((t) => t === type);
        }
    };

    const onPreExistingFile = (name: string) => {
        return new Promise<boolean>((resolve, reject) => {
            _modalService.confirm(`A file named ${name} already exists.  Click OK to overwrite.`, {
                confirmText: 'OK',
                cancelText: 'Cancel',
                onConfirm: () => resolve(true),
                onCancel: () => reject(false)
            });
        });
    };

    const _downloadFile = (file: IDataPackageFileDescriptor) => {
        setIsLoading(true);
        _toastService.info('Preparing file for download...');
        //send data count

        if (datasetMetaDataDoi && datasetMetaDataDoi != 'DOINotCreated' && _canUserSendDataCount(studyOrgId)) {
            const fileIdentity = file.datapackageDoiFileNumber || file.name;
            _studiesService.sendDataCountCollectionData(datasetMetaDataDoi, fileIdentity).subscribe(
                (sent) => {
                    LoggerService.log(`Download count is ${sent}`);
                },
                (error) => {
                    LoggerService.log(`Download count is errored:  ${error}`);
                }
            );
        }

        const dataPackageIdToUse = secondaryDataPackageId && file.publicAccessGranted ? secondaryDataPackageId : dataPackageId;
        _dataPackageFileService
            .downloadFileUri(dataPackageIdToUse, file.name, recordFileDownload || false, useSecureStorage)
            .pipe(first())
            .subscribe(
                (downloadLink) => {
                    downloadFileToBrowser(downloadLink, file.name);
                    setIsLoading(false);
                },
                (error) => {
                    setIsLoading(false);
                    _handleError('Unable to download file.', error);
                }
            );
    };
    const _removeFromPublicAccess = (activeFile: IDataPackageFileDescriptor) => {
        // remove from public access — routed through the serialized queue as an
        // `update` op so it can't race a concurrent addUpdateFile on the same etag.
        // Note: this writes to the secondary package, so we enqueue a plain update
        // (the consumer's update branch calls addUpdateFile on dataPackageId); for
        // the secondary-package path we keep the direct call but funnel ordering via
        // the queue is not needed since it targets a different package/etag.
        const updatedFile = {
            ...activeFile,
            publicAccessGranted: false,
            publicAccessDisabled: true
        };
        return _dataPackageService.addUpdateFile(secondaryDataPackageId, updatedFile).pipe(
            first(),
            map((dp) => {
                onSecondaryDataPackageUpdated && onSecondaryDataPackageUpdated(dp);
            })
        );
    };

    const _deleteFile = (file: IDataPackageFileDescriptor): Promise<void> => {
        // Secondary (public-access) files are a different server call against the
        // secondary package — not part of the primary package's etag race — so they
        // keep their own direct call. Everything else is enqueued as a `delete` op so
        // it serializes behind any pending update/type-change for the same file.
        const isSecondaryFile = secondaryFilesToShow?.find((f) => f.name === file.name);
        if (isSecondaryFile) {
            _addPendingFile(file.name);
            return new Promise<void>((resolve) => {
                _removeFromPublicAccess(file)
                    .pipe(finalize(() => _removePendingFile(file.name)))
                    .subscribe({
                        next: () => resolve(),
                        error: (err) => _handleError('Error deleting file.', err)
                    });
            });
        }

        const enqueueDelete = () => _serverFileDelete(file, `Error deleting file ${file.name}.`);

        if (showDeleteDialog) {
            return _handleFileDeleteWarning(file).then(() => enqueueDelete());
        }

        enqueueDelete();
        return Promise.resolve();
    };

    // Producer side of the serialized file-operation queue. Every path that needs to
    // persist a file descriptor change (add, type change, public-availability
    // toggle, upload start/complete) calls this rather than addUpdateFile directly,
    // so all writes flow through the single concatMap consumer above and never run
    // concurrently. onSuccess runs only after the queued write actually succeeds.
    //
    // Pass ONLY the fields being changed in `changes` — never a pre-spread copy of the
    // whole descriptor. The consumer merges `changes` onto the freshest descriptor when the
    // item is dequeued, which is what keeps a queued write from clobbering fields another
    // write has since updated. (DEV-711)
    function _serverFileUpdate(
        file: IDataPackageFileDescriptor,
        changes: Partial<IDataPackageFileDescriptor>,
        errorMessage: string,
        onSuccess?: (dp: IDataPackage, file: IDataPackageFileDescriptor) => void,
        options?: IServerFileUpdateOptions
    ) {
        fileUpdateQueueRef.current.next({ op: { kind: 'update' }, file, changes, errorMessage, onSuccess, ...options });
    }

    // Producer for delete ops. Routes deletes through the same serialized queue as
    // updates so a delete can't race a concurrent addUpdateFile on the same etag.
    function _serverFileDelete(
        file: IDataPackageFileDescriptor,
        errorMessage: string,
        onSuccess?: (dp: IDataPackage, file: IDataPackageFileDescriptor) => void,
        options?: IServerFileDeleteOptions
    ) {
        fileUpdateQueueRef.current.next({ op: { kind: 'delete' }, file, errorMessage, onSuccess, ...options });
    }

    const _changeFileType = (file: IDataPackageFileDescriptor, type: string) => {
        // Only the changed fields — the rest of the descriptor is resolved at dequeue time so this
        // cannot re-persist a stale upload status off the grid row's snapshot. (DEV-711)
        const changes: Partial<IDataPackageFileDescriptor> = { type };

        _handleFileTypeChangeForPubliclyAvailable(type, file, changes, true);

        _serverFileUpdate(file, changes, `Unable to change file type for ${file.name}.`);
    };

    const _updateFileLimitReached = () => {
        if (fileLimit) {
            // Always write the value — never guard on the current `fileLimitReached` state.
            // This runs from `_setFiles`, and the serialized file-update queue calls `_setFiles`
            // through the closure captured when its subscription was created (the useEffect keyed
            // on dataPackageId), so the `fileLimitReached` read here is the mount-render value and
            // is permanently false. A "did it change?" guard therefore compared false against false
            // on the delete path and silently skipped the reset, leaving the flag stuck on true and
            // the drop zone hidden until a refetch. React already bails out of re-rendering when the
            // value is unchanged, so the guard bought nothing. (DEV-562)
            setFileLimitReached(filesRefCount.current >= fileLimit);
        }
    };

    const setAdditionalFileProps = (additionalFileProps: any) => {
        additionalFilePropsRef.current = additionalFileProps;
    };

    const handlePubliclyAvailableChanged = (file: IDataPackageFileDescriptor, publicAccessGranted: boolean, localChange = false) => {
        // only fire server update if this is not a local file change only
        if (localChange) return;

        _serverFileUpdate(file, { publicAccessGranted }, `Unable to change Public Availability for ${file.name ?? 'N/A'}.`);
    };

    const onFileDeleted = (file: IDataPackageFileDescriptor) => {
        return _deleteFile(file);
    };

    const onFileDownload = (file: IDataPackageFileDescriptor) => {
        _downloadFile(file);
    };

    const onFileTypeChanged = (file: IDataPackageFileDescriptor, newFileType: string) => {
        _changeFileType(file, newFileType);
    };

    const onFileUploadStart = (uploaderId: string, fileName: string, sizeInKb: number) => {
        // register this upload as in-flight so the app-wide navigation guard warns before leaving (DEV-485)
        uploadActivityService.reportUploadStarted(dataPackageId, uploaderId);

        // reset progress so the bar starts at 0 (covers retries that reuse the uploaderId)
        emitFileProgress(uploaderId, 0);

        const preExistingFile = filesRef.current.find((f) => f.name === fileName);
        // RQ-148 (DEV-496): use the shared sanitizer so the displayed name matches the name the
        // server stores (the off-the-shelf sanitize-filename left '+' and ',' untouched).
        // Redundant for the useFileUploader path since DEV-892 made that hook sanitize every name it
        // emits, but this is a public callback and the sanitizer is idempotent, so it stays as cheap
        // defence for any other caller.
        const sanitizedFileName = sanitizeUploadFileName(fileName);

        // create a new file descriptor to add to the local array
        let fileDescriptor: IDataPackageFileDescriptor = null;
        // Fields this write should persist. A brand-new file needs the whole descriptor (nothing exists
        // server-side yet); re-uploading over an existing file only resets the upload-tracking fields, so
        // it must not carry the rest of the stale snapshot and revert e.g. a concurrent type change. (DEV-711)
        let changes: Partial<IDataPackageFileDescriptor> = null;

        const baseProps = {
            percentUploaded: 0,
            isComplete: false,
            isError: false,
            uploadedByUserEmail: user.email,
            uploadedByUserId: user.userId,
            uploadedByUserName: user.name,
            sizeInKb,
            id: uuidv4(),
            uploaderId
        };

        if (!preExistingFile) {
            fileDescriptor = {
                name: sanitizedFileName,
                type: 'Unknown', // default to unknown file type
                ...baseProps,
                ...additionalFilePropsRef.current // add any additional props that were set by the caller
            };
            changes = fileDescriptor;
            // update the local files with the newly added file prior to sending to the server
            // if it errors out it will be zeroed out progress and noted as errored
            _setFiles([...(filesRef.current || []), fileDescriptor]);
        } else {
            fileDescriptor = {
                ...preExistingFile,
                ...baseProps,
                ...additionalFilePropsRef.current
            };
            // An overwrite still resets only the upload-tracking fields (DEV-711), but the props the
            // caller set describe *this* upload, not the previous one, so they have to be re-applied.
            // Without them the descriptor keeps the first uploader's organization, and a Vivli Admin
            // overwriting another org's feedback file left the Organization Name showing that first
            // org. (DEV-392)
            changes = { ...baseProps, ...additionalFilePropsRef.current };

            const index: number = filesRef.current.findIndex((item) => item.name === fileName);
            if (index >= 0) {
                filesRef.current[index] = fileDescriptor;
            }
            // update the local files with the newly added file prior to sending to the server
            // if it errors out it will be zeroed out progress and noted as errored
            // Copy the array: passing filesRef.current itself hands setFiles the same reference it
            // already holds, so React bails out of the re-render and the grid keeps rendering the
            // replaced descriptor until some later update happens to create a new array. (DEV-711)
            _setFiles([...filesRef.current]);
        }

        // update the data package with the new file descriptor
        // routed through the serialized queue so it cannot run concurrently with other addUpdateFile calls
        _serverFileUpdate(fileDescriptor, changes, 'Error adding file.', (_dp, file) => {
            // fire the file added event to bubble it up if needed
            onFileAdded && onFileAdded(file);
        });
    };

    const onFileUploadCompleted = (id: string, name: string) => {
        // upload finished; clear it from the in-flight registry (DEV-485)
        uploadActivityService.reportUploadEnded(dataPackageId, id);

        // The bytes are done, but the row keeps showing the progress bar until the server completion
        // write below returns (the controls key off isComplete, not percentUploaded). So pin the
        // published value at 100 rather than clearing it here — clearing while the bar is still mounted
        // left every re-subscribe resolving to 0. Cleanup happens in the write's onSuccess, which is
        // exactly when the row leaves the in-progress state and the bar unmounts. (DEV-711)
        emitFileProgress(id, 100);

        // Locate the file by uploaderId to resolve its name; the rest of the descriptor is merged in at
        // dequeue time, so completing an upload can no longer revert a type change made while this write
        // was queued. (DEV-711)
        const completedFile: IDataPackageFileDescriptor = filesRef.current.find((f) => f.uploaderId === id);
        if (completedFile) {
            _serverFileUpdate(
                completedFile,
                { percentUploaded: 100, isComplete: true },
                `File upload not completed for file ${name ?? 'N/A'}.`,
                () => clearFileProgress(id)
            );
        } else {
            // The descriptor this upload belongs to is gone from local state, so there is nothing to mark
            // complete. The overwrite-while-uploading path used to land here every time and is now blocked
            // in onFileValidation, but a row deleted mid-upload can still reach it, so this stays as a
            // safety net. It is a toast plus a log rather than the blocking red modal it used to be: the
            // modal put an internal uploaderId GUID in front of the user and told them nothing actionable.
            // (DEV-744)
            LoggerService.warn(`File upload completed but no local descriptor matched, file: ${name}, uploader id ${id}. The completed flag was not written.`);
            _toastService.error(
                `File “${name}” may not have finished uploading. Please check the file list and upload it again if it is missing.`,
                { autoClose: 10000, hideProgressBar: false, closeOnClick: true }
            );
        }
    };

    const onFileUploadError = (id: string, name: string, error: string) => {
        // upload no longer in flight; clear it from the registry (DEV-485)
        uploadActivityService.reportUploadEnded(dataPackageId, id);

        const badFileError = `One or more files you attempted to upload to this export folder are prohibited under current export policies.`;
        const existingFile = filesRef.current.find((f) => f.uploaderId === id);
        if (!existingFile) {
            return;
        }

        // Persist the error through the serialized queue (mirroring onFileUploadCompleted)
        // rather than a local-only _updateFile. This (a) makes isError survive a navigate-away /
        // reopen and (b) guarantees the error write is applied after the onFileUploadStart write
        // that was enqueued earlier — without this ordering, the late-resolving start write would
        // re-apply its stale isError:false snapshot and flip the row back to "Uploading".
        // Do NOT add a separate immediate local _updateFile here, or that flicker returns.
        //
        // applyLocallyOnFailure is what makes the failure visible when the cause was the network:
        // this write is an HTTP call over the same dead connection the upload just died on, so it
        // fails too, and without the flag the row kept showing "Uploading" forever while a modal
        // announced a network error. Routing the local apply through the queue (rather than doing it
        // up front) keeps the ordering guarantee above intact. (DEV-478)
        _serverFileUpdate(existingFile, { percentUploaded: 0, isError: true }, `File upload failed for file ${name ?? 'N/A'}.`, undefined, {
            applyLocallyOnFailure: true,
            suppressErrorModal: true
        });

        // drop the published progress value; the row is leaving the in-progress state, and a retry
        // republishes 0 from onFileUploadStart
        clearFileProgress(id);

        //for now, limit error display to the one case of
        // bad file types in results requests
        if (error?.includes(badFileError)) {
            _modalService.error(String(error));
        } else {
            // Non-blocking: the row itself carries the Failed state and the Retry button, so this
            // just makes sure a user who has scrolled away still notices. Only name the connection
            // as the cause when it actually was — an HTTP rejection is not fixed by checking WiFi.
            // (DEV-478)
            const isNetworkError = error?.startsWith(NETWORK_ERROR_PREFIX);
            const advice = isNetworkError ? 'Check your connection, then use Retry.' : 'Use Retry to try again.';
            _toastService.error(`Upload failed for ${name ?? 'this file'}. ${advice}`);
        }
    };

    const onFileUploadProgress = (id: string, name: string, percentUploaded: number) => {
        const existingFile = filesRef.current.find((f) => f.uploaderId === id);
        if (!existingFile) {
            return;
        }

        // Publish progress through the ref-based pub/sub instead of updating `files` state.
        // The grid row data is intentionally left untouched so the controls cell (and the
        // Cancel button within it) does not re-render on every tick — only the subscribed
        // UploadProgressBar updates. Completion/error still flow through the normal state
        // updates (onFileUploadCompleted / onFileUploadError), which is what swaps the
        // displayed control set. (DEV-479)
        emitFileProgress(id, percentUploaded);
    };

    // Captures the live uploader API so cancel can abort/dequeue the underlying request.
    const onUploaderInit = (api: IFileUploader) => {
        uploaderApiRef.current = api;

        // Expose a "cancel everything in flight" handler to the app-wide navigation guard (DEV-485).
        // Route each in-flight file through onFileUploadCancelled (rather than the uploader's raw
        // no-arg cancel) so cancelling also removes the row and deletes the server-side descriptor.
        // A raw abort would leave the descriptor persisted with isComplete:false, so the row would
        // reload stuck on "Uploading". Only files with a live uploaderId that are not yet complete/
        // errored are in flight.
        uploadActivityService.registerCanceler(dataPackageId, () => {
            const inFlightFiles = (filesRef.current || []).filter((f) => f.uploaderId && !f.isComplete && !f.isError);
            inFlightFiles.forEach((f) => onFileUploadCancelled(f));
        });
    };

    // Cancels an in-progress (or still-queued) upload for a single file. Aborts the
    // underlying request via the uploader API, removes the row from local state, keeps
    // the file-limit counter accurate, best-effort removes any descriptor already
    // persisted server-side (onFileUploadStart adds it via the serialized queue), and
    // confirms with a toast. Immediate — no confirmation dialog.
    const onFileUploadCancelled = (file: IDataPackageFileDescriptor, toastMessage?: string) => {
        // upload no longer in flight; clear it from the registry (DEV-485). Keyed on uploaderId, so a
        // reloaded failed row (no uploaderId) is a harmless no-op and cannot clear a real upload.
        uploadActivityService.reportUploadEnded(dataPackageId, file.uploaderId);

        // abort the in-flight XHR and/or remove the file from the uploader's pending queue.
        // Only when we have an uploaderId — a reloaded/re-opened failed row has none (it is
        // client-only), and cancel(undefined) hits the uploader's "cancel ALL" branch, which
        // would abort every other in-flight upload.
        if (file.uploaderId) {
            uploaderApiRef.current?.cancel(file.uploaderId);
            // drop any published progress value for the aborted upload
            clearFileProgress(file.uploaderId);
        }

        // remove the row immediately from local state. Key on uploaderId when present
        // (in-session upload); fall back to name for a reloaded descriptor (no uploaderId) —
        // filtering by an undefined uploaderId would match and drop every reloaded row.
        // File names are unique within a package (the grid uses getRowId = name).
        _setFiles((filesRef.current || []).filter((f) => (file.uploaderId ? f.uploaderId !== file.uploaderId : f.name !== file.name)));

        // keep the file-limit counter accurate (mirrors the increment in onFileValidation).
        // Done here rather than left to the queue's delete branch because that branch only runs on a
        // successful server delete, and a cancelled upload whose descriptor was never persisted gets a
        // failed delete — the counter would drift upward every time. The delete branch skips its own
        // decrement when the row is already gone, so this cannot double-count. (DEV-746)
        filesRefCount.current = Math.max(0, filesRefCount.current - 1);

        // Server cleanup goes through the serialized queue, NOT a direct deleteFile call. (DEV-746)
        // onFileUploadStart enqueues the descriptor write on that same queue, so a direct call here
        // could run FIRST and be undone by the start write landing after it — leaving a descriptor
        // persisted for an upload that was aborted with its blocks staged but never committed. The
        // file then rendered as present with no blob behind it, and Submit / Download failed later.
        // concatMap gives the ordering guarantee the error path has had since DEV-478.
        //
        // The consumer sets the returned data package and fires onFileRemoved, replacing what the old
        // direct subscribe did here. suppressErrorModal because a missing descriptor is an expected
        // outcome (the start write may never have persisted); the queue logs it instead of popping a
        // modal at a user who just cancelled on purpose.
        _serverFileDelete(file, `Cancelled upload server cleanup skipped for ${file.name}.`, undefined, {
            suppressErrorModal: true
        });

        _toastService.info(toastMessage ?? `Upload cancelled: ${file.name}`);
    };

    const onFileUploadRetry = (file: IDataPackageFileDescriptor) => {
        if (!file?.uploaderId) {
            return;
        }

        const existingFile = filesRef.current?.find((f) => f.uploaderId === file.uploaderId);
        if (!existingFile) {
            return;
        }

        _updateFile({
            ...existingFile,
            percentUploaded: 0,
            isComplete: false,
            isError: false
        });

        uploaderApiRef.current?.retry(file.uploaderId);
    };

    const onAllFilesCompleted = (succeededFileIds: string[], failedFileIds: string[]) => {
        LoggerService.log(`All files completed. Succeeded: ${succeededFileIds.length}, Failed: ${failedFileIds.length}`);
        _updateFileLimitReached();
    };

    const onFileValidation = (name: string) => {
        // validate number to check if this is over the limit
        if (fileLimit && filesRefCount.current + 1 > fileLimit) {
            // file was over the limit, pop a warning
            _toastService.error(`You have reached the maximum number of files allowed (${fileLimit}).`);

            // deny file upload by returning false
            return false;
        }

        // validate the file extension
        const parts = name.split('.');
        const fileExtension = ('.' + name.split('.')[parts.length - 1]).toLowerCase();

        const isAllowedExtension = fileExtensionsRef.current?.some((file: IFileExtension) => {
            return fileExtension === file.extension.toLowerCase() && file.allowDocumentUpload;
        });

        if (fileExtensionsRef.current && fileExtensionsRef.current.length > 0 && !isAllowedExtension) {
            _toastService.error(
                `File “${name}” was rejected because this file type is not allowed on the Vivli Platform. Please contact Vivli support if you believe this file type should be allowed.`,
                { autoClose: 10000, hideProgressBar: false, closeOnClick: true }
            );
            LoggerService.debug(`File ${name} rejected due to disallowed extension: ${fileExtension}`);
            return false;
        }

        // as early as possible increment the file count
        filesRefCount.current += 1;

        // retrieve pre-existing file if available
        const preExistingFile = filesRef.current?.find((f) => f.name === name);

        // An overwrite replaces the existing descriptor in place, uploaderId and all (see
        // onFileUploadStart). If the first upload is still transferring, that erases the id every one of
        // its callbacks looks itself up by: its progress and error handlers silently no-op, and its
        // completion falls into the "Completed flag not set" branch of onFileUploadCompleted. Both
        // uploads also keep writing the same blob, since neither the first XHR nor its staged blocks are
        // cancelled. So refuse the second upload outright while the first is in flight, rather than
        // offering an overwrite we cannot honour. Backing out the count matches the overwrite branch
        // below — either way this is not another new file being added. (DEV-744)
        if (preExistingFile && uploadActivityService.isUploadActive(dataPackageId, preExistingFile.uploaderId)) {
            filesRefCount.current -= 1;
            _toastService.error(
                `File “${name}” is still uploading. Wait for it to finish, or cancel it, before uploading it again.`,
                { autoClose: 10000, hideProgressBar: false, closeOnClick: true }
            );
            LoggerService.debug(`File ${name} rejected: an upload for it is already in flight (uploaderId ${preExistingFile.uploaderId}).`);
            return false;
        }

        // if we have a pre-existing file, we need to validate it, otherwise we can just use the file from the local array
        // this is due to a situation where there are multiple organizations uploading files to the same data package
        if (onPreExistingFile && preExistingFile) {
            //BACK OUT THAT INCREMENT IF THE FILE ALREADY EXISTS!!
            //Whether we overwrite or not, it's not another new file being added
            filesRefCount.current -= 1;
            return onPreExistingFile(name);
        }

        if (onFileExtensionWarning && showFileWarning) {
            const parts = name.split('.');
            const fileExtension = '.' + name.split('.')[parts.length - 1];

            const fileTypesToWarnFor = fileExtensionsRef.current?.filter((file: IFileExtension) => {
                return file.warnForDocumentUpload === true;
            });

            const warnForFileType = fileTypesToWarnFor?.some((file: IFileExtension) => {
                return fileExtension === file.extension;
            });

            if (warnForFileType) {
                return onFileExtensionWarning();
            }
        }

        // no validations ran, return file is valid
        return new Promise<boolean>((res) => res(true));
    };

    const onPubliclyAvailableChanged = (file: IDataPackageFileDescriptor, isPubliclyAvailable: boolean) => {
        handlePubliclyAvailableChanged(file, isPubliclyAvailable);
    };

    const getDataPackage = () => {
        setIsLoading(true);

        _dataPackageService
            .getDataPackage(dataPackageId)
            .pipe(first())
            .subscribe({
                next: (dp) => {
                    setDataPackage(dp);

                    // Reconcile stale in-progress descriptors on load: an upload does not resume across
                    // a reload/navigation, so any descriptor that comes back still mid-upload (not
                    // complete, not errored) has no live upload behind it and must not render as a
                    // perpetual "Uploading" row. Surface it as failed so the user can retry or remove it.
                    // Client-side only (not persisted), so a concurrent uploader in another tab/org is
                    // not affected server-side. (DEV-485)
                    const reconciledFiles = (dp.dataPackageFileDescriptors || []).map((file) =>
                        !file.isComplete && !file.isError ? { ...file, isError: true } : file
                    );
                    _setFiles(reconciledFiles);

                    // set initial file count
                    filesRefCount.current = dp.dataPackageFileDescriptors.length;
                },
                error: (error) => {
                    _handleError('Cannot get the Data Package', error);

                    // set data package to null so that loading states work properly
                    setDataPackage(null);
                },
                complete: () => setIsLoading(false)
            });
    };

    // A descriptor with no (or an unparseable) updatedDateTime is a just-started upload - the server
    // hasn't stamped it yet - so it belongs at the top of a newest-first list. Resolving to a number
    // here, rather than calling moment() inside the comparator, keeps the comparator from ever
    // returning NaN. (DEV-489)
    const _fileSortKey = (file: IDataPackageFileDescriptor) => {
        const parsed = file?.updatedDateTime ? moment(file.updatedDateTime).valueOf() : NaN;
        return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
    };

    const _compareFilesByDateDesc = (a: IDataPackageFileDescriptor, b: IDataPackageFileDescriptor) => {
        const aKey = _fileSortKey(a);
        const bKey = _fileSortKey(b);

        // Compare explicitly instead of subtracting: two concurrent in-progress uploads both key to
        // Infinity, and Infinity - Infinity is NaN. An inconsistent comparator doesn't just misplace
        // that one row - it can leave the whole array out of order.
        if (aKey !== bKey) {
            return aKey > bKey ? -1 : 1;
        }

        // Only tie-break by name when both rows carry a real timestamp. Two un-stamped rows are
        // in-progress uploads: returning 0 lets Array.sort's stability keep them in the order they were
        // added, instead of reshuffling them alphabetically - which is what made a batch of uploads
        // render oldest-first until the page was reloaded. (DEV-489)
        if (!Number.isFinite(aKey)) {
            return 0;
        }

        // Tie-break by name so files sharing a timestamp (a bulk upload lands within one tick) keep a
        // stable, reproducible order across renders.
        return (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
    };

    const _sortFiles = (filesToSort: IDataPackageFileDescriptor[]) => {
        if (!sortFilesByDate || !filesToSort) {
            return filesToSort;
        }

        // Copy before sorting: Array.sort is in place, and filesToSort is the `files` state array
        // (the same reference filesRef.current holds), so sorting it directly mutates state during
        // render.
        return [...filesToSort].sort(_compareFilesByDateDesc);
    };

    const getSortedFiles = (files: IDataPackageFileDescriptor[]) => {
        if (secondaryFilesToShow) {
            const distinctFiles = secondaryFilesToShow.concat(
                (files || []).filter((item2) => !secondaryFilesToShow.some((item1) => item1.name === item2.name))
            );
            return _sortFiles(distinctFiles);
        }

        return _sortFiles(files);
    };

    const sortedFiles = useMemo(() => getSortedFiles(files), [files, secondaryFilesToShow, sortFilesByDate]);

    // True while ANY file in the package has not finished uploading — failed or still in flight —
    // regardless of its file type. hasRequiredTypes is not enough on its own: it only asks whether each
    // *required* type has a completed file, so a failed file left on the default 'Unknown' type
    // satisfied nothing, blocked nothing, and Submit stayed enabled. The submit then reached the server
    // and was rejected there with "did not upload properly". (DEV-746)
    //
    // Read the server descriptors rather than the local `files` array, for the same reason
    // _hasCompletedFileOfType does: the server copy still carries isComplete:false for an upload that
    // never finished, so this survives a reload without relying on the client-side reconciliation in
    // getDataPackage.
    //
    // Consequence worth knowing: on a data package shared between organizations, another org's in-flight
    // upload will hold Submit closed until it finishes. That matches how hasRequiredTypes already
    // behaves for required-typed files, and the package genuinely is not fully uploaded yet.
    const hasIncompleteUploads = useMemo(
        () => !!dataPackage?.dataPackageFileDescriptors?.some((file) => isFileUploadFailed(file) || isFileUploadInProgress(file)),
        [dataPackage?.dataPackageFileDescriptors]
    );

    useEffect(() => {
        if (!dataPackageId) {
            return;
        }

        getDataPackage();
    }, [dataPackageId]);

    // Stop tracking this data package's uploads once it unmounts (or its id changes), so the
    // navigation guard's "any upload in progress" signal cannot leak a stale in-flight entry (DEV-485).
    useEffect(() => {
        return () => {
            uploadActivityService.unregister(dataPackageId);
        };
    }, [dataPackageId]);

    useEffect(() => {
        if (!publiclyAvailableOrgId || !readOnly) {
            return;
        }

        _getPubliclyAvailableOrg();
    }, [publiclyAvailableOrgId, readOnly]);

    useEffect(() => {
        if (!dataPackage) {
            return;
        }

        _validateRequiredTypes();

        // only fire on ready function once
        if (!initializedRef.current) {
            initializedRef.current = true;
            onDataPackageReady && onDataPackageReady(dataPackage);
        }
    }, [dataPackage]);

    useEffect(() => {
        // no server side update completed, don't fire an update
        if (!overrideDataPackage || overrideDataPackage._etag === dataPackage._etag) return;

        setDataPackage(overrideDataPackage);
        _setFiles(overrideDataPackage.dataPackageFileDescriptors);
    }, [overrideDataPackage]);

    useEffect(() => {
        // use a stateless ref due to a closure issue with fine uploader
        if (!config) {
            return;
        }
        fileExtensionsRef.current = config.fileExtensions;
    }, [config]);

    return {
        dataPackage,
        files: sortedFiles,
        publiclyAvailableOrg,
        isLoading,
        hasRequiredTypes,
        hasIncompleteUploads,
        fileLimitReached,
        isFilePending,
        getDataPackage,
        setDataPackage,
        setAdditionalFileProps,
        onPreExistingFile,
        onFileDeleted,
        onFileDownload,
        onFileTypeChanged,
        onFileUploadCompleted,
        onFileUploadError,
        onFileUploadStart,
        onFileUploadProgress,
        onFileUploadCancelled,
        onFileUploadRetry,
        subscribeFileProgress,
        onUploaderInit,
        onAllFilesCompleted,
        onFileValidation,
        onPubliclyAvailableChanged
    };
};
